import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Globe,
  ArrowRight,
  BookOpen,
  LifeBuoy,
  Mail,
  Layers,
  Copy,
  Check,
  MessagesSquare,
  Star,
  X,
} from 'lucide-react';
import { Container, PageHeader, SplitView } from '@vnetwork/ui/layout';
import { Stat, StatGroup, StatusPill, EmptyState, type StatusTone } from '@vnetwork/ui/data';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Badge,
  SegmentedControl,
  IconButton,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import { WafFreePromo } from '@/components/promos/WafFreePromo';
import { openLiveChat } from '@/components/livechat/LiveChatWidget';
import { HelpTooltip } from '@/pages/services/components/HelpTooltip';
import styles from './OverviewPage.module.css';

/* SVG icons for card headers — imported as URL assets (rspack asset module) */
import iconYourServices from '@/assets/overview-icons/your-services.svg';
import iconCdnTraffic from '@/assets/overview-icons/cdn-traffic.svg';
import iconTopDomain from '@/assets/overview-icons/top-domain.svg';
import iconServicesInUse from '@/assets/overview-icons/services-in-use.svg';
import iconByProduct from '@/assets/overview-icons/by-product.svg';
import iconRecentServices from '@/assets/overview-icons/recent-services.svg';
/* Product row icons (By product card & Recent services) */
import iconCloudInstances from '@/assets/overview-icons/cloud-instances.svg';
import iconMultiCdn from '@/assets/overview-icons/multi-cdn-orchestration.svg';
import iconManagedK8s from '@/assets/overview-icons/managed-kubernetes.svg';
import iconWaap from '@/assets/overview-icons/waap.svg';
import { getCampaign } from '@/lib/attribution';
import { trackCampaignLanding, trackOnboardingStart } from '@/lib/analytics-events';
import { useAuth } from '@/auth/AuthProvider';
import { usePinnedLeaves, usePinnedServices } from '@/components/layout/pinned-services';
import { useMyServices, useRecentServices, useRecordServiceAccess, serviceProviderScope } from '@/api/services';
import { useCentralSeries, useDomainUsage, periodToRange } from '@/api/manticore/analytics';
import { useDomainListAll } from '@/api/manticore/domains';
import { useInstanceSummary } from '@/api/instances';
import { OPENNEBULA_SCOPE } from '@/api/opennebula';
import { useKubernetesSummary, KUBERNETES_SCOPE } from '@/api/kubernetes';
import { useCredits } from '@/api/billing';
import { useMyInvitations } from '@/api/teams';
import { categoryOfScope, serviceRoute, categoryByKey } from './services/categories';
import { formatCurrency } from '@/lib/format-currency';
import { formatBytes, formatCount } from '@/pages/services/_shared/analytics-format';
import { isWaapCapable } from '@/pages/services/waap/waap-service';
import { isWafCapable } from '@/pages/services/waf/waf-service';
import { brand } from '@/config/brand';

/* ============================================================================
 * Overview dashboard — real data. Services + category split (v3 /services),
 * cloud instances (org-scoped /instances), CDN domains + traffic (/cdn/analytics),
 * billing credit (DataCentral) and pending team invitations (/me/invitations).
 * ========================================================================== */

type Range = '24h' | '7d' | '30d';

const CAT_COLOR: Record<string, string> = {
  streaming: 'var(--vn-color-blue-500)',
  cdn: 'var(--vn-accent-solid)',
  compute: 'var(--vn-color-green-400)',
  kubernetes: 'var(--vn-color-blue-400)',
  databases: 'var(--vn-color-amber-400)',
  storage: 'var(--vn-color-orange-400)',
  management: 'var(--vn-color-neutral-400)',
  security: 'var(--vn-color-red-400)',
  other: 'var(--vn-fg-subtle)',
};

const RUNNING = 'var(--vn-success-solid)';
const CDN_ACCENT = 'var(--vn-accent-solid)';

const muted: React.CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };
const railHeading: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-xs)',
  fontWeight: 'var(--vn-font-weight-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--vn-fg-subtle)',
  margin: `0 0 var(--vn-space-2)`,
};

/** Rail cluster — uppercase label + content, NO divider (mock rail uses clean
 *  20px gaps between sections, not rules). The 8px label→content gap comes from
 *  railHeading's own margin-bottom. */
function RailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 style={railHeading}>{title}</h3>
      {children}
    </div>
  );
}

/** Org-ID field — a nested white (bg-surface) box inside the muted Account card:
 *  label on top, value + copy button in a row below (matches the mock rail). */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--vn-space-2)',
        background: 'var(--vn-bg-surface)',
        borderRadius: 'var(--vn-radius-md)',
        padding: 'var(--vn-space-2) var(--vn-space-3)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>{label}</div>
        <div
          style={{
            fontSize: 'var(--vn-font-size-md)',
            fontWeight: 'var(--vn-font-weight-semibold)',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--vn-fg-default)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </div>
      </div>
      <IconButton
        aria-label={copied ? 'Copied' : `Copy ${label}`}
        variant="ghost"
        size="sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* clipboard unavailable */
          }
        }}
      >
        {copied ? <Check size={14} color="var(--vn-success-solid)" /> : <Copy size={14} />}
      </IconButton>
    </div>
  );
}

/** Small 30px icon shown before CardTitle in card headers. */
function CardIcon({ src, alt = '' }: { src: string; alt?: string }) {
  return <img src={src} alt={alt} width={30} height={30} className={styles.cardIcon} />;
}

/** Plain blue text link for card-header actions (mock uses <a>, not a button). */
function HeaderLink({
  children,
  onClick,
  arrow = true,
}: {
  children: React.ReactNode;
  onClick: () => void;
  arrow?: boolean;
}) {
  return (
    <button className={styles.headerLink} onClick={onClick}>
      {children}
      {arrow && <ArrowRight size={14} />}
    </button>
  );
}

/**
 * Quick-access block for the services the user pinned (via the topbar star).
 * Hidden when nothing is pinned. Each row deep-links into the console; the ✕
 * unpins it (also removes it from the sidebar's Pinned section).
 */
function PinnedServicesSection() {
  const navigate = useNavigate();
  const pinned = usePinnedLeaves();
  const { toggle } = usePinnedServices();
  if (pinned.length === 0) return null;
  return (
    <div style={{ borderTop: '1px solid var(--vn-border-subtle)', padding: 'var(--vn-space-4)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-2)',
          marginBottom: 'var(--vn-space-3)',
        }}
      >
        <Star size={15} color="var(--vn-fg-muted)" />
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-semibold)',
            color: 'var(--vn-fg-muted)',
          }}
        >
          Pinned services
        </span>
      </div>
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {pinned.map((s) => (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-1)',
                border: '1px solid var(--vn-border-default)',
                borderRadius: 'var(--vn-radius-md)',
                background: 'var(--vn-bg-surface)',
                paddingRight: 'var(--vn-space-2)',
              }}
            >
              <button
                type="button"
                onClick={() => navigate(s.route)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-2)',
                  padding: 'var(--vn-space-2) var(--vn-space-3)',
                  border: 'none',
                  background: 'none',
                  color: 'var(--vn-fg-default)',
                  font: 'inherit',
                  fontSize: 'var(--vn-font-size-md)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Star size={14} fill="currentColor" color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.label}
                </span>
              </button>
              <IconButton
                aria-label={`Unpin ${s.label}`}
                variant="ghost"
                size="sm"
                onClick={() => toggle(s.id)}
              >
                <X size={14} />
              </IconButton>
            </div>
          ))}
        </div>
    </div>
  );
}

export default function OverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [range, setRange] = useState<Range>('24h');

  const { data: services = [], isLoading: svcLoading } = useMyServices();
  const credits = useCredits();
  const invitations = useMyInvitations();

  const computeIds = useMemo(
    () => services.filter((s) => serviceProviderScope(s) === OPENNEBULA_SCOPE).map((s) => s.id),
    [services],
  );
  const inst = useInstanceSummary(computeIds);

  const k8sIds = useMemo(
    () => services.filter((s) => serviceProviderScope(s) === KUBERNETES_SCOPE).map((s) => s.id),
    [services],
  );
  const k8s = useKubernetesSummary(k8sIds);

  const bandwidth = useCentralSeries({ domainId: 'all', period: range, metric: 'bandwidth' });
  const requests = useCentralSeries({ domainId: 'all', period: range, metric: 'requests' });
  const hitRatio = useCentralSeries({ domainId: 'all', period: range, metric: 'request_hit_ratio' });

  // Top domains: the account's REAL CDN domains (registry), enriched with traffic
  // for the selected window. A domain with no traffic yet still appears (0 volume)
  // — the "No domains yet" empty state is driven by the domain REGISTRY, not by
  // traffic, so an onboarded-but-idle domain is never hidden. Usage ranks them.
  const usageRange = useMemo(() => {
    const { from, to } = periodToRange(range);
    return { from, to };
  }, [range]);
  const domainUsage = useDomainUsage(usageRange);
  const allDomains = useDomainListAll();
  // Rows are an ANALYTICS view (Requests/Bandwidth/Cache all come from traffic),
  // so they're sourced purely from usage — a domain with no traffic has nothing
  // to rank. The registry (below) only distinguishes the empty state: "no traffic
  // yet" (domains exist) vs "no domains yet" (truly none).
  const topDomains = useMemo(
    () =>
      [...(domainUsage.data ?? [])]
        .sort((a, b) => b.volume_bytes - a.volume_bytes)
        .slice(0, 5)
        .map((u) => ({ name: u.domain_name, volume_bytes: u.volume_bytes })),
    [domainUsage.data],
  );
  const hasAnyDomain = (allDomains.data?.length ?? 0) > 0;

  const firstName = (user?.name || user?.email || '').split(/[ @]/)[0];

  // One-shot redirect for utm_campaign=waf_free arrivals: routes the user into
  // the guided onboarding wizard. sessionStorage flag prevents re-triggering on
  // subsequent loads within the same session (idempotent — no loop risk).
  // WafFreePromo is intentionally left in place; a redirected user just skips it.
  useEffect(() => {
    if (
      getCampaign() === 'waf_free' &&
      !sessionStorage.getItem('partnercfg.onboardingRedirected')
    ) {
      sessionStorage.setItem('partnercfg.onboardingRedirected', '1');
      // Funnel entry: campaign arrival → auto-routed into the guided wizard.
      trackCampaignLanding('waf_free');
      trackOnboardingStart('campaign_redirect', 'waf_free');
      navigate('/services/waap/onboarding', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derived real metrics ------------------------------------------------
  const byCategory = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of services) {
      const key = categoryOfScope(serviceProviderScope(s));
      m[key] = (m[key] ?? 0) + 1;
    }
    return m;
  }, [services]);

  const categorySlices = useMemo(
    () =>
      Object.entries(byCategory)
        .map(([key, value]) => ({
          name: categoryByKey(key)?.title ?? 'Other',
          value,
          color: CAT_COLOR[key] ?? CAT_COLOR.other,
        }))
        .sort((a, b) => b.value - a.value),
    [byCategory],
  );

  // Prefer backend-tracked access history ("Recent services" = most recently
  // opened). Falls back to a created_at sort until the user has opened any
  // service (fresh accounts have no access stats yet).
  const { data: accessed = [] } = useRecentServices({ sort: 'recent', limit: 5 });
  const recordAccess = useRecordServiceAccess();
  const recent = useMemo(() => {
    if (accessed.length > 0) return accessed;
    return [...services]
      .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
      .slice(0, 5);
  }, [accessed, services]);

  const bwSeries = bandwidth.data?.series ?? [];
  const totalBandwidth = bwSeries.reduce((sum, p) => sum + (p.value || 0), 0);
  const totalRequests = (requests.data?.series ?? []).reduce((sum, p) => sum + (p.value || 0), 0);
  const hitPoints = hitRatio.data?.series ?? [];
  const cacheHit =
    hitPoints.length > 0
      ? Math.round((hitPoints.reduce((s, p) => s + (p.value || 0), 0) / hitPoints.length) * 100)
      : null;
  // Plot bandwidth in a single auto-picked unit (KB/MB/GB/…) so the chart axis
  // and tooltip stay short instead of showing raw byte counts.
  const bwUnit = useMemo(() => {
    const peak = bwSeries.reduce((max, p) => Math.max(max, p.value || 0), 0);
    // Traffic volume uses 1000-based (SI) units, not the 1024 disk convention.
    const units: [string, number][] = [
      ['B', 1],
      ['KB', 1000],
      ['MB', 1000 ** 2],
      ['GB', 1000 ** 3],
      ['TB', 1000 ** 4],
      ['PB', 1000 ** 5],
    ];
    let chosen = units[0];
    for (const u of units) if (peak >= u[1]) chosen = u;
    return { name: chosen[0], div: chosen[1] };
  }, [bwSeries]);
  const bwChart = useMemo(
    () => bwSeries.map((p) => ({ t: p.timestamp, v: (p.value || 0) / bwUnit.div })),
    [bwSeries, bwUnit],
  );

  const creditBalance = credits.data?.balance?.total_remaining;
  const invite = invitations.data?.[0];
  const orgId = user?.organizationId || services[0]?.organization_id || '';

  // ---- WAAP onboarding progress (drives description tail + hero) -----------
  const hasEdgeSecurity = services.some(
    (s) => (isWaapCapable(s) || isWafCapable(s)) && s.status?.toLowerCase() === 'active',
  );
  // Mirrors WafFreePromo's stepper: step 1 = org exists; steps 2–3 track the WAAP
  // service lifecycle (domain+SSL configured on creation); step 4 = edge security
  // live. A fresh account with no WAAP service has 3 steps left (only org done) —
  // NOT inferred from unrelated CDN domains.
  const hasWaapService = services.some((s) => isWaapCapable(s) || isWafCapable(s));
  const waapStepsLeft = [Boolean(orgId), hasWaapService, hasWaapService, hasEdgeSecurity].filter(
    (d) => !d,
  ).length;

  // ---- "By product" rows — data-driven from the account's REAL services ------
  // One row per category the account actually has services in (never a fixed
  // placeholder list). Compute/CDN/Kubernetes keep their rich live meta; every
  // other category shows a generic "N services" + status derived from real
  // service statuses. Empty account ⇒ no rows (handled in render).
  const productRows = useMemo(() => {
    const rows: {
      key: string;
      name: string;
      icon: string;
      helpDesc: string;
      meta: string;
      tone: StatusTone;
      status: string;
      route: string;
    }[] = [];
    for (const [key, count] of Object.entries(byCategory)) {
      if (count <= 0) continue;
      const title = categoryByKey(key)?.title ?? 'Other';
      const svcWord = `${count} service${count > 1 ? 's' : ''}`;
      if (key === 'compute') {
        rows.push({
          key,
          name: 'Cloud Instances',
          icon: iconCloudInstances,
          helpDesc: 'Virtual machines you provision and scale on demand, billed per hour of runtime.',
          meta: `${inst.data?.total ?? 0} instances · ${inst.data?.running ?? 0} running`,
          tone: (inst.data?.error ?? 0) > 0 ? 'warning' : 'success',
          status: (inst.data?.error ?? 0) > 0 ? 'Degraded' : 'Operational',
          route: '/services',
        });
      } else if (key === 'cdn') {
        rows.push({
          key,
          name: 'Multi-CDN Orchestration',
          icon: iconMultiCdn,
          helpDesc: 'Routes traffic across multiple CDN vendors and fails over automatically when one degrades.',
          meta: cacheHit != null ? `${cacheHit}% cache hit` : svcWord,
          tone: 'success',
          status: 'Operational',
          route: '/services/cdn',
        });
      } else if (key === 'kubernetes') {
        rows.push({
          key,
          name: 'Managed Kubernetes',
          icon: iconManagedK8s,
          helpDesc: 'Managed control plane and worker nodes — you deploy workloads, we run the cluster.',
          meta: `${k8s.data?.clusters ?? 0} clusters · ${k8s.data?.nodes ?? 0} nodes`,
          tone: (k8s.data?.clusters ?? 0) > 0 ? 'success' : 'muted',
          status: (k8s.data?.clusters ?? 0) > 0 ? 'Healthy' : 'None',
          route: '/services',
        });
      } else {
        const active = services.filter(
          (s) => categoryOfScope(serviceProviderScope(s)) === key && s.status?.toLowerCase() === 'active',
        ).length;
        rows.push({
          key,
          name: title,
          icon: iconByProduct,
          helpDesc: categoryByKey(key)?.description ?? `Your ${title} services.`,
          meta: svcWord,
          tone: active > 0 ? 'success' : 'muted',
          status: active > 0 ? 'Operational' : 'Provisioning',
          route: '/services',
        });
      }
    }
    return rows;
  }, [byCategory, services, inst.data, k8s.data, cacheHit]);

  // ---- System health pill ---------------------------------------------------
  // Degraded when any cloud instance is in error state.
  const systemDegraded = (inst.data?.error ?? 0) > 0;

  return (
    <Container size="xl">
      <PageHeader
        icon={<LayoutDashboard size={22} />}
        title={firstName ? `Welcome back, ${firstName}` : 'Overview'}
        description={
          'Your services in use — at a glance.' +
          (!svcLoading && waapStepsLeft > 0
            ? ` ${waapStepsLeft} step${waapStepsLeft === 1 ? '' : 's'} left to protect your first domain.`
            : '')
        }
        actions={
          <span className={systemDegraded ? styles.statusPillDegraded : styles.statusPillOperational}>
            <span className={styles.statusPillDot} />
            {systemDegraded ? 'Degraded' : 'All systems operational'}
          </span>
        }
      />

      {/* WAF Free promo — shown by default, dismissible per session.
          Gated by the brand campaign toggle so whitelabel tenants can hide it. */}
      {brand.promos.wafFree && <WafFreePromo />}

      <SplitView
        asideWidth="320px"
        aside={
          <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            <RailSection title="Account">
              {/* Filled muted card — matches mock (no border/shadow, bg-muted fill) */}
              <div className={styles.railFilledCard}>
                <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                    <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user?.name || 'Partner account'}
                    </span>
                    <span style={{ ...muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email || '—'}</span>
                  </div>
                  {orgId ? <CopyField label="Organization ID" value={String(orgId)} /> : null}
                </div>
              </div>
            </RailSection>

            {brand.features.credit && (
              <RailSection title="Billing">
                {/* Filled brand-subtle card — matches mock (no border/shadow) */}
                <div className={styles.railBillingCard}>
                  {/* amount row — no wallet icon, matches mock (20px value + "credit") */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                    {credits.isLoading ? (
                      <Skeleton style={{ height: 22, width: 90 }} />
                    ) : (
                      <span style={{ fontSize: 'var(--vn-font-size-xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
                        {formatCurrency(creditBalance ?? 0)}
                      </span>
                    )}
                    <span style={muted}>credit</span>
                  </div>
                  {brand.features.payments && (
                    <button
                      className={styles.ctaBrandBtn}
                      onClick={() => navigate('/account/billing')}
                    >
                      Add credit
                    </button>
                  )}
                  {/* centered plain link — matches mock (no ghost button / arrow) */}
                  <button
                    onClick={() => navigate('/account/billing')}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                      marginTop: 'var(--vn-space-2)',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 'var(--vn-font-size-sm)',
                      fontWeight: 'var(--vn-font-weight-medium)',
                      color: 'var(--vn-primary-text)',
                    }}
                  >
                    View billing
                  </button>
                </div>
              </RailSection>
            )}

            {invite && (
              <Alert tone="info" title="Pending team invitation">
                You've been invited to join <strong>{invite.team_name}</strong>
                {invitations.data && invitations.data.length > 1
                  ? ` (+${invitations.data.length - 1} more)`
                  : ''}
                .
                <div style={{ marginTop: 'var(--vn-space-2)' }}>
                  <Button
                    size="sm"
                    onClick={() => navigate('/teams/invitations/' + invite.id)}
                  >
                    Review invitation
                  </Button>
                </div>
              </Alert>
            )}

            {(brand.features.documentation || brand.features.livechat) && (
            <RailSection title="Helpful resources">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
                {/* Filled muted rows — matches mock (bg-muted, hover → border-default) */}
                {brand.features.documentation && (
                <a href={brand.urls.docs} target="_blank" rel="noreferrer" className={styles.railResourceRow}>
                  <Globe size={16} color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>Multi-CDN Orchestration</div>
                    <div style={{ ...muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Domains, cache, WAF &amp; TLS</div>
                  </div>
                  <ArrowRight size={14} color="var(--vn-fg-subtle)" style={{ flexShrink: 0 }} />
                </a>
                )}
                {brand.features.documentation && (
                  <a href={brand.urls.docs} target="_blank" rel="noreferrer" className={styles.railResourceRow}>
                    <BookOpen size={16} color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>All documentation</div>
                    </div>
                    <ArrowRight size={14} color="var(--vn-fg-subtle)" style={{ flexShrink: 0 }} />
                  </a>
                )}
                {/* Live chat — the entry point on mobile, where the floating bubble is hidden. */}
                {brand.features.livechat && (
                  <button
                    type="button"
                    onClick={openLiveChat}
                    className={`${styles.railResourceRow} ${styles.railResourceButton}`}
                  >
                    <MessagesSquare size={16} color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>Live chat</div>
                      <div style={{ ...muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Chat with our support team</div>
                    </div>
                    <ArrowRight size={14} color="var(--vn-fg-subtle)" style={{ flexShrink: 0 }} />
                  </button>
                )}
              </div>
            </RailSection>
            )}

            {brand.features.support && (
              <RailSection title="Contact">
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)', ...muted }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <LifeBuoy size={14} /> {brand.emails.support}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <Mail size={14} /> {brand.emails.sales}
                  </span>
                </div>
              </RailSection>
            )}
          </div>
        }
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* ① Your services (pinned services docked at the bottom of this card) */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <HeaderLink arrow={false} onClick={() => navigate('/services')}>
                All services
              </HeaderLink>
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconYourServices} />
              <CardTitle>Your services</CardTitle>
            </div>
          </CardHeader>
          <div className={styles.serviceStatGrid}>
            {/* Total services */}
            <div className={styles.serviceStatCell}>
              <div className={styles.serviceStatLabel}>
                <span className={styles.serviceStatDotPrimary} />
                Total services
              </div>
              <div className={styles.serviceStatValue}>
                {svcLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : String(services.length)}
              </div>
              <div className={styles.serviceStatSub}>
                across {Object.keys(byCategory).length} categor{Object.keys(byCategory).length === 1 ? 'y' : 'ies'}
              </div>
            </div>
            {/* Cloud instances */}
            <div className={styles.serviceStatCell}>
              <div className={styles.serviceStatLabel}>
                <span className={styles.serviceStatDotMuted} />
                Cloud instances
              </div>
              <div className={styles.serviceStatValue}>
                {inst.isLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : String(inst.data?.total ?? 0)}
              </div>
              {!inst.isLoading && (inst.data?.total ?? 0) === 0 ? (
                <button className={styles.serviceStatLink} onClick={() => navigate('/services')}>
                  Create instance
                </button>
              ) : (
                <div className={styles.serviceStatSub}>
                  {inst.data?.running ?? 0} running
                </div>
              )}
            </div>
            {/* Kubernetes */}
            <div className={styles.serviceStatCell}>
              <div className={styles.serviceStatLabel}>
                <span className={styles.serviceStatDotMuted} />
                Kubernetes
              </div>
              <div className={styles.serviceStatValue}>
                {k8s.isLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : String(k8s.data?.clusters ?? 0)}
              </div>
              {!k8s.isLoading && (k8s.data?.clusters ?? 0) === 0 ? (
                <button className={styles.serviceStatLink} onClick={() => navigate('/services')}>
                  Create cluster
                </button>
              ) : (
                <div className={styles.serviceStatSub}>
                  {k8s.data?.nodes ?? 0} nodes
                </div>
              )}
            </div>
          </div>
          <PinnedServicesSection />
        </Card>

        {/* ② CDN traffic */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <SegmentedControl
                size="sm"
                value={range}
                onValueChange={(v) => setRange(v as Range)}
                items={[
                  { value: '24h', label: '24h' },
                  { value: '7d', label: '7d' },
                  { value: '30d', label: '30d' },
                ]}
              />
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconCdnTraffic} />
              <CardTitle>CDN traffic</CardTitle>
              <HelpTooltip
                title="CDN traffic"
                description="Requests, bandwidth and cache hit ratio measured at the edge across all your domains."
                docsHref={brand.urls.docs}
              />
            </div>
            <CardDescription>Requests, bandwidth &amp; cache hit ratio across all domains.</CardDescription>
          </CardHeader>
          <CardBody>
            <StatGroup columns={3} style={{ marginBottom: 'var(--vn-space-4)' }}>
              <Stat
                label="Requests"
                value={requests.isLoading ? <Skeleton style={{ height: 28, width: 70 }} /> : formatCount(totalRequests)}
                dotColor={CDN_ACCENT}
              />
              <Stat
                label="Bandwidth"
                value={bandwidth.isLoading ? <Skeleton style={{ height: 28, width: 90 }} /> : formatBytes(totalBandwidth)}
              />
              <Stat
                label="Cache hit"
                value={hitRatio.isLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : cacheHit != null ? `${cacheHit}%` : '—'}
                dotColor={RUNNING}
              />
            </StatGroup>
            {bandwidth.isLoading ? (
              <Skeleton style={{ height: 240 }} />
            ) : bwChart.length > 0 ? (
              <Chart
                type="area"
                height={240}
                data={bwChart}
                xKey="t"
                series={[{ key: 'v', label: `Bandwidth (${bwUnit.name})`, color: CDN_ACCENT }]}
                showGrid
                valueFormatter={(v) => `${Number(v ?? 0).toFixed(2)} ${bwUnit.name}`}
              />
            ) : (
              /* Ghost preview — shown when no CDN traffic recorded yet */
              <div className={styles.chartGhost}>
                <svg viewBox="0 0 900 210" preserveAspectRatio="none" className={styles.chartGhostSvg}>
                  <defs>
                    <linearGradient id="ghostA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--vn-brand-solid)" stopOpacity="0.30" />
                      <stop offset="100%" stopColor="var(--vn-brand-solid)" stopOpacity="0.02" />
                    </linearGradient>
                    <linearGradient id="ghostB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--vn-primary-solid)" stopOpacity="0.20" />
                      <stop offset="100%" stopColor="var(--vn-primary-solid)" stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  <path d="M0 176H900M0 132H900M0 88H900M0 44H900" stroke="var(--vn-bg-muted)" strokeWidth="1" />
                  <path d="M0 150C60 148 90 122 150 118S240 138 300 128 390 78 450 84 540 122 600 110 690 64 750 74 840 106 900 96V210H0Z" fill="url(#ghostA)" />
                  <path d="M0 150C60 148 90 122 150 118S240 138 300 128 390 78 450 84 540 122 600 110 690 64 750 74 840 106 900 96" fill="none" stroke="var(--vn-danger-solid)" strokeOpacity="0.6" strokeWidth="2" />
                  <path d="M0 178C60 172 100 162 160 164S250 180 310 172 400 140 460 146 550 172 610 164 700 132 760 138 850 158 900 152V210H0Z" fill="url(#ghostB)" />
                  <path d="M0 178C60 172 100 162 160 164S250 180 310 172 400 140 460 146 550 172 610 164 700 132 760 138 850 158 900 152" fill="none" stroke="var(--vn-primary-solid)" strokeOpacity="0.7" strokeWidth="2" strokeDasharray="5 4" />
                </svg>
                <div className={styles.chartGhostOverlay}>
                  <span className={styles.chartGhostBadge}>
                    <span className={styles.chartGhostDot} />
                    Preview
                  </span>
                  <p className={styles.chartGhostText}>
                    This is how the chart will look once your domain starts receiving traffic. Real data appears within 5 minutes of onboarding.
                  </p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ②.5 Top domains */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <span className={styles.topDomainsCaption}>By traffic, last 24h</span>
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconTopDomain} />
              <CardTitle>Top domains</CardTitle>
            </div>
          </CardHeader>
          {domainUsage.isLoading || allDomains.isLoading ? (
            <CardBody><Skeleton style={{ height: 160 }} /></CardBody>
          ) : (
            /* Responsive scroll wrapper — min-width prevents column collapse on narrow viewports */
            <div className={styles.topDomainsScroll}>
              <div className={styles.topDomainsMinWidth}>
                {/* Table header */}
                <div className={styles.topDomainsHeaderRow}>
                  <div>Domain</div>
                  <div className={styles.topDomainsColRight}>Requests</div>
                  <div className={styles.topDomainsColRight}>Bandwidth</div>
                  <div className={styles.topDomainsColRight}>Cache</div>
                </div>
                {/* Data rows or skeleton rows */}
                {topDomains.length > 0 ? (
                  topDomains.map((d) => (
                    <div key={d.name} className={styles.topDomainsRow}>
                      <div className={styles.topDomainsDomainCell}>
                        <span className={styles.topDomainsDot} />
                        <span className={styles.topDomainsDomainName}>{d.name}</span>
                      </div>
                      <div className={styles.topDomainsColRight}>—</div>
                      <div className={styles.topDomainsColRight}>{formatBytes(d.volume_bytes)}</div>
                      <div className={styles.topDomainsColRight}>—</div>
                    </div>
                  ))
                ) : (
                  <>
                    {[150, 118, 170, 134, 156].map((w, i) => (
                      <div key={i} className={styles.topDomainsRow}>
                        <div className={styles.topDomainsDomainCell}>
                          <span className={styles.topDomainsSkeletonDot} />
                          <Skeleton style={{ height: 9, width: w, borderRadius: 3 }} />
                        </div>
                        <div className={styles.topDomainsColRight}><Skeleton style={{ height: 9, width: 46, borderRadius: 3 }} /></div>
                        <div className={styles.topDomainsColRight}><Skeleton style={{ height: 9, width: 42, borderRadius: 3 }} /></div>
                        <div className={styles.topDomainsColRight}><Skeleton style={{ height: 9, width: 36, borderRadius: 3 }} /></div>
                      </div>
                    ))}
                  </>
                )}
                {/* Empty overlay — "no traffic yet" when domains exist (registry),
                    "no domains yet" only when the account has zero domains. */}
                {topDomains.length === 0 && (
                  <div className={styles.topDomainsOverlay}>
                    <span className={styles.topDomainsOverlayIcon}><Globe size={21} /></span>
                    <div className={styles.topDomainsOverlayTitle}>
                      {hasAnyDomain ? 'No traffic yet' : 'No domains yet'}
                    </div>
                    <div className={styles.topDomainsOverlayDesc}>
                      {hasAnyDomain
                        ? 'Your domains haven’t received traffic yet. This table ranks them by traffic — data appears within 5 minutes.'
                        : 'This table will rank your domains by traffic as soon as you onboard your first one.'}
                    </div>
                    <button
                      className={styles.topDomainsOverlayCta}
                      onClick={() => navigate(hasAnyDomain ? '/services/cdn/home' : '/services/cdn')}
                    >
                      {hasAnyDomain ? 'View domains' : 'Onboarding Domain Now'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* ③ Services by category */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <HeaderLink onClick={() => navigate('/services')}>All services</HeaderLink>
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconServicesInUse} />
              <CardTitle>Services in use</CardTitle>
            </div>
            <CardDescription>Distribution of services by category.</CardDescription>
          </CardHeader>
          <CardBody>
              {svcLoading ? (
                <Skeleton style={{ height: 200 }} />
              ) : categorySlices.length === 0 ? (
                <EmptyState icon={<Layers size={24} />} title="No services yet" description="Provisioned services will appear here." />
              ) : (
                <div className={styles.servicesInUseGrid}>
                  <PieChart
                    donut
                    height={200}
                    data={categorySlices}
                    centerLabel={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
                          {services.length}
                        </div>
                        <div style={muted}>services</div>
                      </div>
                    }
                  />
                  <div className={styles.legendList}>
                    {categorySlices.map((c) => {
                      const pct = services.length > 0 ? Math.round((c.value / services.length) * 100) : 0;
                      return (
                        <div key={c.name} className={styles.legendRow}>
                          <span className={styles.legendDot} style={{ background: c.color }} />
                          <span className={styles.legendLabel}>{c.name}</span>
                          <span className={styles.legendCount}>{c.value}</span>
                          <span className={styles.legendBarTrack}>
                            <span className={styles.legendBarFill} style={{ width: `${pct}%`, background: c.color }} />
                          </span>
                          <span className={styles.legendPct}>{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </CardBody>
        </Card>

        {/* ④ By product */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <HeaderLink onClick={() => navigate('/services')}>All services</HeaderLink>
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconByProduct} />
              <CardTitle>By product</CardTitle>
            </div>
            <CardDescription>Operational status of each product line.</CardDescription>
          </CardHeader>
          {productRows.length === 0 ? (
            <div style={{ borderTop: '1px solid var(--vn-border-subtle)' }}>
              <EmptyState
                title="No services yet"
                description="Activate a service and its operational status will show here."
                size="sm"
              />
            </div>
          ) : (
            productRows.map((p) => (
              <div
                key={p.key}
                role="button"
                tabIndex={0}
                onClick={() => navigate(p.route)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(p.route)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-3)',
                  padding: 'var(--vn-space-3) var(--vn-space-4)',
                  cursor: 'pointer',
                  borderTop: '1px solid var(--vn-border-subtle)',
                }}
              >
                <img src={p.icon} alt="" width={28} height={28} style={{ flexShrink: 0, display: 'block' }} />
                <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{p.name}</span>
                <HelpTooltip title={p.name} description={p.helpDesc} docsHref={brand.urls.docs} />
                <span style={{ ...muted, flex: 1 }}>{p.meta}</span>
                <StatusPill tone={p.tone}>{p.status}</StatusPill>
                <ArrowRight size={14} color="var(--vn-fg-subtle)" />
              </div>
            ))
          )}
        </Card>

        {/* ④.5 Kubernetes Resources — rich section (legacy @console parity) */}
        {k8s.data?.firstCluster && (
          <Card flat>
            <CardHeader
              actions={
                <StatusPill tone={k8s.data.firstCluster.status === 'active' ? 'success' : 'warning'}>
                  {k8s.data.firstCluster.status}
                </StatusPill>
              }
            >
              <CardTitle>Kubernetes — {k8s.data.firstCluster.name}</CardTitle>
              <CardDescription>Cluster resources & add-ons at a glance.</CardDescription>
            </CardHeader>
            <CardBody>
              <StatGroup columns={5}>
                <Stat label="Clusters" value={k8s.data.clusters} />
                <Stat label="Nodes" value={k8s.data.nodes} dotColor={RUNNING} />
                <Stat label="Pods" value={k8s.data.pods} dotColor={CDN_ACCENT} />
                <Stat label="vCPU" value={k8s.data.cpuCores} />
                <Stat label="Memory" value={`${k8s.data.memoryGB} GB`} />
              </StatGroup>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--vn-space-2)',
                  marginTop: 'var(--vn-space-4)',
                }}
              >
                <Badge tone="neutral" appearance="outline" size="sm">
                  {k8s.data.firstCluster.version}
                </Badge>
                {k8s.data.firstCluster.cni && (
                  <Badge tone="neutral" appearance="outline" size="sm">
                    CNI: {k8s.data.firstCluster.cni}
                  </Badge>
                )}
                {k8s.data.firstCluster.endpoint.fqdn && (
                  <span style={{ ...muted, marginLeft: 'auto' }}>{k8s.data.firstCluster.endpoint.fqdn}</span>
                )}
              </div>

              {k8s.data.firstCluster.addons.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--vn-space-2)',
                    marginTop: 'var(--vn-space-3)',
                  }}
                >
                  {k8s.data.firstCluster.addons.map((a) => (
                    <Badge key={a} tone="info" appearance="subtle" size="sm">
                      {a.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* ⑤ Recent services */}
        <Card flat className={styles.brandCard}>
          <CardHeader
            actions={
              <HeaderLink onClick={() => navigate('/services')}>View all</HeaderLink>
            }
          >
            <div className={styles.cardHeaderInner}>
              <CardIcon src={iconRecentServices} />
              <CardTitle>Recent services</CardTitle>
            </div>
            <CardDescription>The services you've opened most recently.</CardDescription>
          </CardHeader>
          {svcLoading ? (
            <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <EmptyState title="No services yet" description="Provisioned services will show up here." />
          ) : (
            recent.map((s) => {
              const scope = serviceProviderScope(s);
              const cat = categoryByKey(categoryOfScope(scope));
              const route = serviceRoute(scope, s.id) ?? '/services';
              const open = () => {
                recordAccess(s.id);
                navigate(route);
              };
              // Pick a category-appropriate icon; fall back to waap.svg (generic shield)
              const catKey = categoryOfScope(scope);
              const rowIcon =
                catKey === 'security' ? iconWaap
                : catKey === 'compute' ? iconCloudInstances
                : catKey === 'kubernetes' ? iconManagedK8s
                : catKey === 'cdn' ? iconMultiCdn
                : iconWaap;
              const svcLabel = s.service_name || s.provider?.display_name || scope;
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={(e) => e.key === 'Enter' && open()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-3)',
                    padding: 'var(--vn-space-3) var(--vn-space-4)',
                    cursor: 'pointer',
                    borderTop: '1px solid var(--vn-border-subtle)',
                  }}
                >
                  <img src={rowIcon} alt="" width={28} height={28} style={{ flexShrink: 0, display: 'block' }} />
                  <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {svcLabel}
                  </span>
                  <HelpTooltip
                    title={svcLabel}
                    description={cat?.description ?? 'View this service in the console.'}
                  />
                  <Badge tone="neutral" appearance="outline" size="sm">
                    {cat?.title ?? 'Other'}
                  </Badge>
                  <StatusPill tone={(s.status?.toLowerCase() === 'active' ? 'success' : 'muted') as StatusTone}>
                    {s.status || 'Active'}
                  </StatusPill>
                  <ArrowRight size={14} color="var(--vn-fg-subtle)" />
                </div>
              );
            })
          )}
        </Card>
        </div>
      </SplitView>
    </Container>
  );
}
